// ===================================================
// ФАЙЛ: frontend/src/components/Layout/Layout.jsx
// Главный Layout — новый Sidebar + Header + Footer
// ===================================================
import React, { Suspense, lazy, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Sidebar, { SIDEBAR_COLLAPSED_EVENT } from './Sidebar';
import Header from './Header';
import AppFooter from './AppFooter';
import FavoritePagesTabs from './FavoritePagesTabs';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { logoutUser } from '../../store/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { getThemeColors, reloadThemeColors, reloadResourceStyles } from '../../constants/resourceMeta';
import { resolveSidebarThemeCssVars } from '../../utils/sidebarThemeContrast';
import {
  LAYOUT_SIDEBAR_COLLAPSED_FALLBACK_PX,
  LAYOUT_SIDEBAR_EXPANDED_FALLBACK_PX,
} from '../../constants/layoutShellMetrics';
import useScreenLock from '../../hooks/useScreenLock';
import { ScreenLockContext } from '../../contexts/ScreenLockContext';
import { isProtectedSystemCompanyId } from '../../utils/systemCompany';
import { WaySenCompanionProvider } from '../../contexts/WaySenCompanionContext';

const ConsentBlocker = lazy(() => import('../Legal/ConsentBlocker'));
const BetaConsentModal = lazy(() => import('../Legal/BetaConsentModal'));
const CompanyBlockedModal = lazy(() => import('../Legal/CompanyBlockedModal'));
const CompanySetupWizard = lazy(() => import('../Onboarding/CompanySetupWizard'));
const GlobalSearch = lazy(() => import('../GlobalSearch/GlobalSearch'));
const HelperSidebar = lazy(() => import('../HelperSidebar/HelperSidebar'));
const ScreenLock = lazy(() => import('../../pages/Auth/ScreenLock'));
const WaySenCompanionRoot = lazy(() => import('../Companion'));

const OPEN_ONBOARDING_EVENT = 'open_company_setup_wizard';

const DeferredUiChunk = ({ children }) => (
  <Suspense fallback={null}>
    {children}
  </Suspense>
);

// Контент страницы: маршруты подключаются статически в App.js (без route-level lazy) —
// обёртка Suspense вокруг Outlet не нужна; внутренние модалки с lazy держат свой Suspense.
const PageContent = () => {
  const location = useLocation();

  // Уведомляем страницы о навигации (используется, например, в ProductsPage
  // для обновления данных при возврате на страницу).
  // ✅ Только setTimeout(0) — никаких setState и preloadRoute.
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('mb-page-navigation', {
        detail: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          key: location.key,
        }
      }));
    }, 0);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search, location.hash, location.key]);

  return <Outlet />;
};

const CompanyLayout = () => {
  const { progress: onboardingProgress } = useOnboarding();
  const [companyBlockInfo, setCompanyBlockInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const company = useSelector((state) => state.auth.company);
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const isSystemCompany = isProtectedSystemCompanyId(company?.id);
  const hasCompanySettings = !!(company?.settings && typeof company.settings === 'object');
  const onboardingCompleted = hasCompanySettings && company?.settings?.onboarding?.completed === true;
  const hasUser = Boolean(user);
  const companyIsInactive = company?.is_active === false;
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingForced, setOnboardingForced] = useState(false);
  // Онбординг показываем только после получения данных пользователя через Redux
  // (checkAuth → /api/auth/me → setUser/setCompany). Локальный дублирующий
  // axios.get('/api/auth/me') удалён — он давал двойной запрос и +400-800мс
  // на каждом mount Layout без новых данных. См. Pack 2 в audit.
  const meFetched = Boolean(user?.id && hasCompanySettings !== undefined);

  // Состояние sidebar collapsed — синхронизация через кастомный event из Sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('mb_sidebar_collapsed') === 'true';
    } catch (_e) {
      return false;
    }
  });

  // Мобильный режим
  const [isMobile, setIsMobile] = useState(false);

  // Screen Lock (Активность и блокировка экрана — серверный lock-state)
  const screenLockHook = useScreenLock();
  const { isLocked, isEnabled: screenLockEnabled, unlockScreen, serverConfig: lockServerConfig } = screenLockHook;

  // Значение контекста для Header / ProfileDropdown
  const screenLockCtxValue = { isEnabled: screenLockEnabled, isLocked, serverConfig: lockServerConfig };

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setIsMobile(false);
      return undefined;
    }
    // Как Sidebar/CSS: max-width 768px (не innerWidth — расходится из‑за scrollbar)
    const mql = window.matchMedia('(max-width: 768px)');
    const apply = (e) => setIsMobile(e.matches);
    apply(mql);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', apply);
      return () => mql.removeEventListener('change', apply);
    }
    mql.addListener(apply);
    return () => mql.removeListener(apply);
  }, []);

  // Isolate Branding for ВЭЙГПТ/App
  useEffect(() => {
    try {
      if (window.location.hostname.includes('waygpt') || process.env.REACT_APP_IS_WAYGPT === 'true') {
        document.documentElement.setAttribute('data-brand', 'waygpt');
      } else {
        document.documentElement.removeAttribute('data-brand');
      }
    } catch (_e) {
      // safe fallback
    }
  }, []);

  // Слушаем кастомный event из Sidebar для синхронизации ширины
  useEffect(() => {
    const handler = (e) => {
      setSidebarCollapsed(e.detail?.collapsed ?? false);
    };
    window.addEventListener(SIDEBAR_COLLAPSED_EVENT, handler);
    return () => window.removeEventListener(SIDEBAR_COLLAPSED_EVENT, handler);
  }, []);

  // Вызов блокировки из Header теперь обрабатывается внутри useScreenLock

  // isChatPage — legacy AssistantButton удалён; WaySen Companion в Layout


  // Resize при монтировании
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event('resize'));
  }, []);

  /** Синхронное применение токенов rail до первой отрисовки (дефолт = THEME_COLORS / CSS shell). */
  const applySidebarThemeToRoot = useCallback((themeColors) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const shellFallbackBg = getComputedStyle(root).getPropertyValue('--shell-sidebar-fallback-bg').trim();
    const sidebarVars = resolveSidebarThemeCssVars(themeColors, shellFallbackBg);
    Object.entries(sidebarVars).forEach(([key, value]) => {
      if (value != null && value !== '') root.style.setProperty(key, value);
    });
  }, []);

  // До paint: совпадает с глобальными дефолтами; затем async-эффект подменит на ответ API (компания/платформа).
  useLayoutEffect(() => {
    applySidebarThemeToRoot(getThemeColors());
  }, [applySidebarThemeToRoot]);

  // Загрузка темы компании и глобальной темы с сервера.
  // Guard на повторное выполнение: после `checkAuth.fulfilled` Redux обновляет
  // `company` (новая ссылка на объект), и эффект перезапускался даже когда
  // company.id фактически не менялся → 2 лишних GET (resource-styles + theme-colors)
  // на каждом маунте Layout без причины.
  const lastLoadedThemeKeyRef = useRef(null);
  useEffect(() => {
    const themeKey = company?.id || '__platform__';
    if (lastLoadedThemeKeyRef.current === themeKey) {
      // Тема уже загружена для этой компании/платформы — только применяем токены к root.
      applySidebarThemeToRoot(getThemeColors());
      return;
    }
    lastLoadedThemeKeyRef.current = themeKey;
    const loadThemeAndStyles = async () => {
      try {
        if (company?.id) {
          await Promise.all([
            reloadResourceStyles(company.id),
            reloadThemeColors(company.id),
          ]);
        } else {
          await Promise.all([
            reloadResourceStyles(),
            reloadThemeColors(),
          ]);
        }

        applySidebarThemeToRoot(getThemeColors());
      } catch (_e) {
        applySidebarThemeToRoot(getThemeColors());
      }
    };
    loadThemeAndStyles();
  }, [company?.id, applySidebarThemeToRoot]);

  const shouldShowModals = isAuthenticated && user && company;

  // Блокировка страниц при неактивной компании
  useEffect(() => {
    if (!companyIsInactive) return;
    const path = location.pathname || '';
    const restrictedPrefixes = [
      '/products',
      '/warehouses',
      '/suppliers',
      '/marketplaces',
      '/connections',
      '/users',
      '/sync',
      '/exchange-log',
    ];
    if (restrictedPrefixes.some((p) => path.startsWith(p))) {
      navigate('/tariffs', { replace: true });
    }
  }, [companyIsInactive, location.pathname, navigate]);

  // Мастер онбординга
  useEffect(() => {
    if (companyBlockInfo) {
      if (!onboardingForced) setOnboardingOpen(false);
      return;
    }
    if (
      onboardingForced
      && shouldShowModals
      && meFetched
      && hasCompanySettings
      && !isSystemCompany
      && onboardingCompleted !== true
    ) {
      setOnboardingOpen(true);
      return;
    }
    if (!onboardingForced) setOnboardingOpen(false);
  }, [companyBlockInfo, onboardingForced, shouldShowModals, meFetched, hasCompanySettings, isSystemCompany, onboardingCompleted]);

  // Ручной запуск мастера
  useEffect(() => {
    const handler = () => { setOnboardingForced(true); setOnboardingOpen(true); };
    window.addEventListener(OPEN_ONBOARDING_EVENT, handler);
    return () => window.removeEventListener(OPEN_ONBOARDING_EVENT, handler);
  }, []);

  // Блокировка компании
  useEffect(() => {
    if (!shouldShowModals) return;
    const handleBlocked = (event) => {
      const { type, data } = event.detail;
      setCompanyBlockInfo({ type, data });
    };
    const handleConsents = (event) => {
      const { type } = event.detail;
      if (type === 'COMPANY_CONSENTS_REQUIRED') {
        setCompanyBlockInfo(null);
        sessionStorage.removeItem('company_block_info');
      }
    };
    window.addEventListener('company_blocked', handleBlocked);
    window.addEventListener('company_consents_granted', handleConsents);

    const stored = sessionStorage.getItem('company_block_info');
    if (stored) {
      try {
        const info = JSON.parse(stored);
        if (info.timestamp && (Date.now() - info.timestamp) < 5 * 60 * 1000) {
          setCompanyBlockInfo({ type: info.type, data: info.data });
        } else {
          sessionStorage.removeItem('company_block_info');
        }
      } catch (_e) { /* ignore */ }
    }
    return () => {
      window.removeEventListener('company_blocked', handleBlocked);
      window.removeEventListener('company_consents_granted', handleConsents);
    };
  }, [shouldShowModals]);

  const handleCompanyBlockClose = () => {
    sessionStorage.removeItem('company_block_info');
    setCompanyBlockInfo(null);
    dispatch(logoutUser());
    navigate('/login');
  };

  // Ширина отступа под sidebar
  // Используем CSS переменные из STYLESYSTEM/layout/sidebar.css для синхронизации размеров
  const getSidebarWidth = useCallback(() => {
    if (isMobile) return 0;
    if (typeof window === 'undefined' || !document.documentElement) {
      // Fallback значения для SSR или до загрузки DOM
      return sidebarCollapsed ? LAYOUT_SIDEBAR_COLLAPSED_FALLBACK_PX : LAYOUT_SIDEBAR_EXPANDED_FALLBACK_PX;
    }

    try {
      // Читаем значения из CSS переменных для точной синхронизации
      const root = getComputedStyle(document.documentElement);
      const expandedWidth = root.getPropertyValue('--sidebar-width-expanded').trim();
      const collapsedWidth = root.getPropertyValue('--sidebar-width-collapsed').trim();

      // Парсим значения (убираем 'px' если есть)
      const expanded = expandedWidth ? parseFloat(expandedWidth.replace('px', '')) : LAYOUT_SIDEBAR_EXPANDED_FALLBACK_PX;
      const collapsed = collapsedWidth ? parseFloat(collapsedWidth.replace('px', '')) : LAYOUT_SIDEBAR_COLLAPSED_FALLBACK_PX;

      return sidebarCollapsed ? collapsed : expanded;
    } catch (_e) {
      // Fallback при ошибке чтения CSS переменных
      return sidebarCollapsed ? LAYOUT_SIDEBAR_COLLAPSED_FALLBACK_PX : LAYOUT_SIDEBAR_EXPANDED_FALLBACK_PX;
    }
  }, [sidebarCollapsed, isMobile]);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Инициализация с fallback значениями
    return isMobile ? 0 : (sidebarCollapsed ? LAYOUT_SIDEBAR_COLLAPSED_FALLBACK_PX : LAYOUT_SIDEBAR_EXPANDED_FALLBACK_PX);
  });

  // Обновляем ширину при изменении состояния sidebar и размера экрана
  useEffect(() => {
    const updateWidth = () => {
      setSidebarWidth(getSidebarWidth());
    };

    updateWidth();

    // Слушаем изменения медиа-запросов для широкоформатных экранов
    const mediaQuery = window.matchMedia('(min-width: 1921px)');
    const handleMediaChange = () => updateWidth();
    mediaQuery.addEventListener('change', handleMediaChange);

    // Также слушаем изменения размера окна на случай изменения CSS переменных
    window.addEventListener('resize', updateWidth);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
      window.removeEventListener('resize', updateWidth);
    };
  }, [getSidebarWidth]);

  // Динамическая высота шапки (Header + FavoritePagesTabs) + высота полосы избранного для выравнивания «Настроить»
  const headerWrapperRef = useRef(null);
  const favoritesBarRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(52);
  const [favoritesHeight, setFavoritesHeight] = useState(40);

  const updateHeaderHeight = useCallback(() => {
    if (headerWrapperRef.current) {
      const h = headerWrapperRef.current.getBoundingClientRect().height;
      setHeaderHeight((prev) => (prev !== h ? h : prev));
    }
    const favEl =
      favoritesBarRef.current ||
      headerWrapperRef.current?.querySelector?.('.favorite-pages-tabs-container');
    if (favEl) {
      const fh = Math.round(favEl.getBoundingClientRect().height);
      // Empty favorites wrapper → fallback 40px so «Настроить» row stays aligned
      const next = fh > 0 ? fh : 40;
      setFavoritesHeight((prev) => (prev !== next ? next : prev));
    } else {
      setFavoritesHeight((prev) => (prev !== 40 ? 40 : prev));
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    if (!headerWrapperRef.current) return undefined;

    const observer = new ResizeObserver(() => {
      updateHeaderHeight();
    });
    observer.observe(headerWrapperRef.current);
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  return (
    <ScreenLockContext.Provider value={screenLockCtxValue}>
      <WaySenCompanionProvider>
      {/* Модалки — не рендерим при заблокированном экране */}
      {isAuthenticated && user && !isLocked && (
        <DeferredUiChunk>
          <BetaConsentModal />
        </DeferredUiChunk>
      )}

      {shouldShowModals && !isLocked && (
        <>
          {!companyBlockInfo && onboardingOpen && (
            <DeferredUiChunk>
              <CompanySetupWizard
                open={onboardingOpen}
                onClose={() => { setOnboardingOpen(false); setOnboardingForced(false); }}
              />
            </DeferredUiChunk>
          )}
          {companyBlockInfo && ['COMPANY_DEACTIVATED', 'COMPANY_CONSENTS_REQUIRED', 'COMPANY_INACTIVE', 'ADMIN_DEACTIVATED'].includes(companyBlockInfo.type) && (
            <DeferredUiChunk>
              <CompanyBlockedModal visible blockType={companyBlockInfo.type} blockData={companyBlockInfo.data || {}} onClose={handleCompanyBlockClose} />
            </DeferredUiChunk>
          )}
        </>
      )}

      {/* Экран блокировки: рендерится ВМЕСТО контента, а не поверх */}
      {isLocked ? (
        <DeferredUiChunk>
          <ScreenLock
            isLocked={isLocked}
            user={user}
            company={company}
            serverConfig={lockServerConfig}
            onUnlock={unlockScreen}
            onLogout={() => {
               unlockScreen();
               dispatch(logoutUser());
               navigate('/login');
            }}
            onSwitchAccount={() => {
               unlockScreen();
               dispatch(logoutUser());
               navigate('/login');
            }}
          />
        </DeferredUiChunk>
      ) : (
        <>
          <div
            className="mb-layout"
            style={{
              '--layout-sidebar-width': `${sidebarWidth}px`,
              '--layout-header-height': `${headerHeight}px`,
              '--layout-favorites-height': `${favoritesHeight}px`,
            }}
          >
            <Sidebar />

            <div className="mb-layout__content-area">
              <div
                ref={headerWrapperRef}
                className="mb-layout__header-wrapper"
              >
                <Header />
                <div ref={favoritesBarRef}>
                  <DeferredUiChunk>
                    <FavoritePagesTabs />
                  </DeferredUiChunk>
                </div>
              </div>

              <main className="mb-layout__main">
                <div className="mb-layout__page-content">
                  {shouldShowModals && !isSystemCompany && !companyBlockInfo && (
                    <DeferredUiChunk>
                      <ConsentBlocker />
                    </DeferredUiChunk>
                  )}
                  <PageContent />
                </div>
                <AppFooter />
              </main>
            </div>

            <div className="mb-layout__helper-anchor" aria-hidden={!isAuthenticated || isMobile}>
              {isAuthenticated && !isMobile && (
                <DeferredUiChunk>
                  <HelperSidebar onboardingProgress={onboardingProgress} />
                </DeferredUiChunk>
              )}
            </div>
          </div>

          <DeferredUiChunk>
            <GlobalSearch />
          </DeferredUiChunk>
          {isAuthenticated && (
            <DeferredUiChunk>
              <WaySenCompanionRoot />
            </DeferredUiChunk>
          )}
        </>
      )}
      </WaySenCompanionProvider>
    </ScreenLockContext.Provider>
  );
};

// Keep company-only hooks and shell components outside the first-login tree.
const Layout = () => {
  const { company, contextReady } = useAuth();
  const location = useLocation();
  if (!contextReady) return null;
  if (['/company/create', '/company/join', '/company/requests'].includes(location.pathname)) return <Outlet />;
  if (!company?.id) return <Navigate to="/company/create" replace />;
  return <CompanyLayout key={String(company.id)} />;
};

export default Layout;
